import { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { usePopularTags, useTagSearch, useTagCategories, useTagStats, useImportTags } from '../hooks'
import type { TagItem } from '../types'
import { IconSearch, IconX, IconSync } from '@/app/components/icons'

export function TagBrowser() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined)

  const { data: stats } = useTagStats()
  const { data: categories } = useTagCategories()
  const { data: popularData, isLoading: isPopularLoading } = usePopularTags(50, selectedCategory)
  const { data: searchData, isLoading: isSearchLoading } = useTagSearch(debouncedQuery, 50, selectedCategory)
  const importMutation = useImportTags()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isSearching = debouncedQuery.length >= 1
  const tags = isSearching ? (searchData?.tags ?? []) : (popularData?.tags ?? [])
  const isLoading = isSearching ? isSearchLoading : isPopularLoading

  // Debounce search input
  const handleInputChange = useCallback((value: string) => {
    setQuery(value)
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value.trim())
      debounceRef.current = null
    }, 300)
  }, [])

  const handleImport = useCallback(() => {
    importMutation.mutate()
  }, [importMutation])

  return (
    <div
      className="min-h-screen bg-tg-bg"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-tg-bg/95 backdrop-blur-sm px-4 py-3">
        <div className="mb-3 flex items-center justify-between">
          <h1
            className="text-lg font-semibold text-tg-text"
          >
            {t('tags.title')}
          </h1>
          <button
            type="button"
            onClick={handleImport}
            disabled={importMutation.isPending}
            className="flex items-center gap-1.5 rounded-xl bg-gradient-to-br from-tg-button to-tg-button/80 px-3 py-2 text-xs font-medium text-tg-button-text shadow-sm shadow-tg-button/20 transition-all duration-200 disabled:opacity-50 active:scale-[0.98]"
          >
            {importMutation.isPending ? (
              <>
                <IconSync className="h-3 w-3 animate-spin" />
                {t('tags.importing')}
              </>
            ) : t('tags.import')}
          </button>
        </div>

        {/* Import result feedback */}
        {importMutation.isSuccess && (
          <div className="animate-fade-in mb-2 rounded-lg bg-tg-secondary px-3 py-2 text-xs text-tg-success">
            {t('tags.importSuccess', {
              fetched: importMutation.data.total_fetched,
              saved: importMutation.data.total_saved,
            })}
          </div>
        )}
        {importMutation.isError && (
          <div className="animate-fade-in mb-2 rounded-lg bg-tg-secondary px-3 py-2 text-xs text-tg-destructive">
            {t('tags.importError')}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <p
            className="mb-3 text-xs text-tg-hint"
          >
            {t('tags.totalTags', { count: stats.total_tags })}
          </p>
        )}

        {/* Search input */}
        <div className="flex items-center gap-2 rounded-xl border border-tg-hint/20 bg-tg-secondary px-3 py-2.5 shadow-sm transition-all duration-150 focus-within:border-[var(--tg-theme-button-color)] focus-within:shadow-md">
          <IconSearch className="h-4 w-4 shrink-0 text-tg-hint" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={t('tags.search')}
            className="flex-1 bg-transparent text-sm text-tg-text outline-none placeholder:text-tg-hint"
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setQuery('')
                setDebouncedQuery('')
              }}
              className="rounded-full p-0.5 text-tg-hint transition-colors hover:text-tg-text active:opacity-70"
            >
              <IconX className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Category filter pills */}
        {categories && categories.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setSelectedCategory(undefined)}
              className="shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-all duration-150 active:scale-[0.96]"
              style={{
                backgroundColor: selectedCategory === undefined
                  ? 'var(--tg-theme-button-color)'
                  : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
                color: selectedCategory === undefined
                  ? 'var(--tg-theme-button-text-color)'
                  : 'var(--tg-theme-text-color)',
                border: selectedCategory === undefined
                  ? '1px solid var(--tg-theme-button-color)'
                  : '1px solid transparent',
              }}
            >
              {t('history.filter.all')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className="shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-all duration-150 active:scale-[0.96]"
                style={{
                  backgroundColor: selectedCategory === cat.id
                    ? `color-mix(in srgb, ${cat.color} 20%, var(--tg-theme-secondary-bg-color))`
                    : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
                  color: selectedCategory === cat.id ? cat.color : 'var(--tg-theme-text-color)',
                  border: selectedCategory === cat.id ? `1px solid ${cat.color}` : '1px solid transparent',
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pb-4">
        {!isSearching && (
          <h2
            className="mb-2 text-sm font-medium text-tg-text"
          >
            {t('tags.popular')}
          </h2>
        )}

        {isLoading && (
          <div className="py-8 text-center">
            <p className="text-tg-hint">
              {t('common.loading')}
            </p>
          </div>
        )}

        {!isLoading && tags.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-tg-hint">
              {t('tags.noResults')}
            </p>
          </div>
        )}

        {!isLoading && tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <TagChip key={tag.name} tag={tag} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function TagChip({ tag }: { readonly tag: TagItem }) {
  const { t } = useTranslation()

  return (
    <div
      className="flex items-center gap-1.5 rounded-lg border border-tg-hint/10 bg-tg-secondary px-2.5 py-1.5 text-xs shadow-sm transition-all duration-150 hover:border-tg-hint/20"
    >
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: tag.category_color || '#6b7280' }}
        title={tag.category_name}
      />
      <span className="text-tg-text">{tag.name}</span>
      <span className="text-tg-hint">
        {t('tags.posts', { count: tag.post_count })}
      </span>
    </div>
  )
}
